<template>
  <ErrorModal />
  <AddCustomer v-if="showAdd === true" :showAdd="showAdd" />
  <div class="customers-grid-container">
    <!-- Customers grid -->
    <div class="dx-card-custom">
      <DxDataGrid
        id="gridContainer"
        :data-source="customers"
        :key-expr="'id'"
        :allow-column-reordering="true"
        :remote-operations="true"
        :show-row-lines="true"
        :show-borders="true"
        ref="dataGridRef"
        :allow-column-resizing="true"
        column-resizing-mode="widget"
        @initialized="filterActiveCustomers(true)"
      >
        <DxToolbar style="width: 200px">
          <DxItem location="before" template="gridHeadingTemplate" />
          <DxItem location="after" template="gridHeadingButtonTemplate" />
          <DxItem
            name="columnChooserButton"
            locate-in-menu="auto"
            location="after"
          />
        </DxToolbar>
        <template #gridHeadingTemplate>
          <div class="row items-center">
            <h1 class="grid-heading">All customers</h1>
            <div class="active-only-filter">
              <span>Show active</span>
              <dx-switch
                :value="showActiveToggle"
                @value-changed="toggleShowActive"
                switched-on-text="Enabled"
                switched-off-text="Disabled"
              />
              <span>Show inactive</span>
              <dx-switch
                :value="showInactiveToggle"
                @value-changed="toggleShowInactive"
                switched-on-text="Enabled"
                switched-off-text="Disabled"
              />
            </div>
          </div>
        </template>
        <template #gridHeadingButtonTemplate>
          <DxButton
            id="addCustomerBtn"
            class="main-btn m-2"
            text="Add new customer"
            styling-mode="contained"
            icon="plus"
            iconPosition="right"
            @click="enableAdd()"
          >
          </DxButton>
        </template>
        <DxHeaderFilter :visible="true" />
        <DxFilterRow :visible="true" />
        <DxPaging :page-size="50" />
        <DxPager
          :visible="true"
          :display-mode="compact"
          :show-navigation-buttons="true"
          :show-info="true"
          :show-page-size-selector="true"
          :info-text="pageInfoText"
        />
        <DxColumnChooser
          enabled="true"
          mode="select"
          height="370"
          width="400"
          title="Choose columns"
        >
          <DxColumnChooserSelection
            allow-select-all="true"
            select-by-click="true"
          />
          <DxPosition
            my="right top"
            at="right bottom"
            of=".dx-datagrid-column-chooser-button"
          />
        </DxColumnChooser>
        <DxColumn
          data-field="name"
          caption="Name"
          sort-order="asc"
          :allow-header-filtering="false"
        />
        <DxColumn
          width="35%"
          data-field="environment"
          caption="Products"
          alignment="left"
          :calculate-cell-value="setProductBadges"
          cell-template="statusCellTemplate"
          :lookup="productFilters"
          filter-type="include"
          :allow-filtering="false"
          :allow-header-filtering="true"
          :header-filter="headerFilterOptions"
          :calculate-filter-expression="customProductFilterExpression"
        />
        <template #statusCellTemplate="{ data }">
          <span
            v-for="badge in data.value"
            :key="badge.value"
            class="status-badge"
            :style="{
              backgroundColor: badge.bgColor,
              color: badge.color,
              padding: '6px',
              marginRight: '5px',
              borderRadius: '5px'
            }"
            :title="badge.hoverText"
          >
            {{ badge.name }}
          </span>
        </template>
        <DxColumn
          width="10%"
          data-field="languageCodeName"
          caption="Language"
          alignment="left"
          cell-template="languageCellTemplate"
          :allow-header-filtering="true"
          :allow-filtering="false"
          filter-type="include"
        />
        <template #languageCellTemplate="{ data }">
          <img
            v-if="data.data.languageCodeName === 'NL'"
            src="https://flagsapi.com/NL/flat/32.png"
            alt="Dutch"
          />
          <img
            v-else-if="data.data.languageCodeName === 'DE'"
            src="https://flagsapi.com/DE/flat/32.png"
            alt="German"
          />
        </template>
        <DxColumn
          data-field="startDate"
          caption="Start Date"
          :visible="false"
          :showInColumnChooser="false"
        />
        <DxColumn
          data-field="endDate"
          caption="End Date"
          :visible="false"
          :showInColumnChooser="false"
        />
        <DxColumn
          data-field="sasStartDate"
          caption="SAS Start Date"
          :visible="false"
          :showInColumnChooser="false"
        />
        <DxColumn
          data-field="sasEndDate"
          caption="SAS End Date"
          :visible="false"
          :showInColumnChooser="false"
        />
        <DxColumn
          width="10%"
          type="buttons"
          caption="Actions"
          :buttons="customerActionButtons"
        />
      </DxDataGrid>
    </div>

    <!-- Deactivate customer popup -->
    <DxPopup
      v-model:visible="deactivatePopupVisible"
      :drag-enabled="false"
      :show-title="false"
      :width="'auto'"
      :height="'auto'"
    >
      <template #content>
        <p class="row items-center">
          <i class="las la-2x la-ban la-fw"></i>
          Do you really want to deactivate this customer?
        </p>
        <div class="row justify-between">
          <DxButton
            class="orange-btn-filled popup-btn"
            text="Yes"
            @click="deactivateCustomer"
          ></DxButton>
          <DxButton
            class="orange-btn popup-btn"
            text="No"
            @click="closeDeactivatePopup"
          ></DxButton>
        </div>
      </template>
    </DxPopup>

    <!-- Remove customer popup -->
    <DxPopup
      v-model:visible="removePopupVisible"
      :drag-enabled="false"
      :show-title="false"
      :width="'auto'"
      :height="'auto'"
    >
      <template #content>
        <p class="row items-center">
          <i class="las la-2x la-trash-alt la-fw"></i>
          Do you really want to remove this customer?
        </p>
        <div class="row justify-between">
          <DxButton
            class="orange-btn-filled popup-btn"
            text="Yes"
            @click="removeCustomer"
          ></DxButton>
          <DxButton
            class="orange-btn popup-btn"
            text="No"
            @click="closeRemovePopup"
          ></DxButton>
        </div>
      </template>
    </DxPopup>
  </div>
</template>

<script setup>
import {
  DxDataGrid,
  DxColumn,
  DxColumnChooser,
  DxColumnChooserSelection,
  DxPosition,
  DxPaging,
  DxPager,
  DxHeaderFilter,
  DxFilterRow,
  DxToolbar,
  DxItem
} from "devextreme-vue/data-grid";
import { ref, onMounted, computed } from "vue";
import DxPopup from "devextreme-vue/popup";
import DxButton from "devextreme-vue/button";
import DxSwitch from "devextreme-vue/switch";
import CustomersService from "../services/customers.service.js";
import AddCustomer from "../components/customers-components/AddCustomer.vue";
import ErrorModal from "../components/customers-components/ErrorModal.vue";
import createCustomStore from "../store/custom.store.js";
import moment from "moment";
import router from "../router/index.js";
import store from "../store/index.js";

onMounted(() => {
  customers.value = createCustomStore("/api/customers", null);
});

const customerActionButtons = [
  {
    hint: "Edit",
    icon: "las la-2x la-pencil-alt la-fw",
    onClick: e => goToEditCustomer(e.row.data.id)
  },
  {
    hint: "Deactivate",
    icon: "las la-2x la-ban la-fw",
    onClick: e => {
      customerId.value = e.row.data.id;
      deactivatePopupVisible.value = true;
    }
  },
  {
    hint: "Remove",
    icon: "las la-2x la-trash-alt la-fw",
    onClick: e => {
      customerId.value = e.row.data.id;
      removePopupVisible.value = true;
    }
  }
];
const productFilters = {
  dataSource: [
    { value: 0, name: "CNT (Content)" },
    { value: 10, name: "ISV" },
    { value: 20, name: "BSN (Business)" },
    { value: 30, name: "SKS" },
    { value: 40, name: "DRV" },
    { value: 50, name: "ISN (ISV no user)" },
    { value: 500, name: "SAS" }
  ],
  valueExpr: "value",
  displayExpr: "name"
};
const headerFilterOptions = {
  allowSelectAll: true,
  // Remove (Blanks) from filter options
  dataSource(options) {
    options.dataSource.postProcess = results => {
      return results.filter(item => item.value !== null && item.value !== "");
    };
  }
};

const customers = ref();
const customerId = ref(null);
const dataGridRef = ref(null);
const deactivatePopupVisible = ref(false);
const removePopupVisible = ref(false);
const showActiveToggle = ref(true);
const showInactiveToggle = ref(false);

const showAdd = computed(() => store.state.customers.showAdd);
const pageInfoText = computed(() => `Page {0} of {1} ({2} items)`);

const deactivateCustomer = async () => {
  await CustomersService.deactivateCustomer(customerId.value);
  refreshGrid();
  closeDeactivatePopup();
};
const closeDeactivatePopup = () => {
  deactivatePopupVisible.value = false;
};
const removeCustomer = async () => {
  await CustomersService.deleteCustomer(customerId.value);
  refreshGrid();
  closeRemovePopup();
};
const closeRemovePopup = () => {
  removePopupVisible.value = false;
};
const getColorBasedOnDate = (startDate, endDate) => {
  if (startDate == null || moment(startDate) > moment()) return "red";
  if (endDate != null && moment(endDate) < moment()) return "red";
  else if (
    endDate != null &&
    moment(endDate) >= moment() &&
    moment(endDate) <= moment().add(2, "months")
  )
    return "lightgreen";
  else return "green";
};
const refreshGrid = () => {
  dataGridRef.value?.instance?.getDataSource().reload();
};
const goToEditCustomer = id => {
  router.push({ name: "EditCustomer", params: { id } });
};
const enableAdd = () => {
  store.commit("customers/enableAdd");
};
const setProductBadges = rowData => {
  const productBadges = [
    {
      value: 0,
      name: "CNT",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "Content"
    },
    {
      value: 500,
      name: "SAS",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "SAS"
    },
    {
      value: 10,
      name: "ISV",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "ISV"
    },
    {
      value: 20,
      name: "BSN",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "Business"
    },
    {
      value: 30,
      name: "SKS",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "SKS"
    },
    {
      value: 40,
      name: "DRV",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "DRV"
    },
    {
      value: 50,
      name: "ISN",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "ISV no user"
    }
  ];

  switch (rowData.environment) {
    case 0: // Default (CNT and SAS)
      updateProductBadge(0, productBadges, rowData.startDate, rowData.endDate);
      updateProductBadge(
        500,
        productBadges,
        rowData.sasStartDate,
        rowData.sasEndDate
      );
      break;
    default:
      // Other envs
      updateProductBadge(
        rowData.environment,
        productBadges,
        rowData.startDate,
        rowData.endDate
      );
      break;
  }

  return productBadges;
};
const updateProductBadge = (
  environmentValue,
  productBadges,
  startDate,
  endDate
) => {
  const productBadge = productBadges.find(b => b.value === environmentValue);
  if (productBadge) {
    productBadge.color = "white";
    productBadge.bgColor = getColorBasedOnDate(startDate, endDate);
  }

  return productBadges;
};
const customProductFilterExpression = (value, operation, target) => {
  if (value === 500) {
    const today = new Date().toISOString();
    const sasActiveFilter = [
      ["sasStartDate", "<=", today],
      "and",
      [["sasEndDate", "=", null], "or", ["sasEndDate", ">=", today]]
    ];

    const filter =
      showActiveToggle.value && !showInactiveToggle.value
        ? [["environment", "=", 0], "and", sasActiveFilter]
        : ["environment", "=", 0];

    return filter;
  }

  return ["environment", operation, value];
};
const toggleShowActive = e => {
  showActiveToggle.value = e.value;
  filterCustomers();
};
const toggleShowInactive = e => {
  showInactiveToggle.value = e.value;
  filterCustomers();
};
const filterCustomers = () => {
  if (showActiveToggle.value && !showInactiveToggle.value)
    filterActiveCustomers();
  else if (!showActiveToggle.value && showInactiveToggle.value)
    filterInactiveCustomers();
  else refreshGridWithoutCustomFilters();
};
const filterActiveCustomers = () => {
  const today = new Date().toISOString();
  const activeFilter = [
    ["startDate", "<=", today],
    "and",
    [["endDate", "=", null], "or", ["endDate", ">=", today]]
  ];
  const sasActiveFilter = [
    ["sasStartDate", "<=", today],
    "and",
    [["sasEndDate", "=", null], "or", ["sasEndDate", ">=", today]]
  ];

  dataGridRef.value.instance.option("filterValue", [
    activeFilter,
    "or",
    sasActiveFilter
  ]);
};
const filterInactiveCustomers = () => {
  const today = new Date().toISOString();
  const inactiveFilter = [
    [["startDate", "=", null], "or", ["startDate", ">", today]],
    "or",
    [["endDate", "<", today]],
    "or",
    [["startDate", "=", null], "and", ["endDate", "=", null]]
  ];
  const sasInactiveFilter = [
    [["sasStartDate", "=", null], "or", ["sasStartDate", ">", today]],
    "or",
    [["sasEndDate", "<", today]],
    "or",
    [["sasStartDate", "=", null], "and", ["sasEndDate", "=", null]]
  ];

  dataGridRef.value.instance.option("filterValue", [
    inactiveFilter,
    "and",
    sasInactiveFilter
  ]);
};
const refreshGridWithoutCustomFilters = () => {
  // Remove custom but keep filters set on the grid
  dataGridRef.value.instance.option("filterValue", null);
  dataGridRef.value.instance.refresh();
};
</script>
